import type { FreeArea, PlatformType, OrientationType } from "@rubyplay/effw-connector";

export type HostSessionPhase =
	| "disconnected"
	| "transport_ready"
	| "configured"
	| "game_ready";

export type HostProviderBootInput = {
	sessionKey: string;
	serverUrl: string;
	gameName: string;
	lang: string;
	operator: string;
	platform: PlatformType;
	orientation: OrientationType;
	integrationType?: string;
};

export type HostSessionConfig = {
	sessionKey: string;
	serverUrl: string;
	gameName: string;
	lang: string;
	currency: string;
	operator: string;
	platform: PlatformType;
	orientation: OrientationType;
	integrationType?: string;
	isHomeButtonEnabled?: boolean;
	isCheatsEnabled: boolean;

	bet?: number;
	subunit?: number;
	funMode?: boolean;
	funModeBalance?: number;
	regulationType?: string;
	iframeContainerId?: string;
};

export type HostSessionState = {
	phase: HostSessionPhase;
	transportReady: boolean;
	configured: boolean;
	startupPreloadRequested: boolean;
	gameReady: boolean;

	config: HostSessionConfig | null;
	configuredFreeAreas: FreeArea[];
	freeAreas: FreeArea[];
	lastUpdatedAtMs: number | null;
};

export type HostSessionSnapshot = {
	readonly phase: HostSessionPhase;
	readonly transportReady: boolean;
	readonly configured: boolean;
	readonly startupPreloadRequested: boolean;
	readonly gameReady: boolean;

	readonly config: Readonly<HostSessionConfig> | null;
	readonly configuredFreeAreas: readonly Readonly<FreeArea>[];
	readonly freeAreas: readonly Readonly<FreeArea>[];
	readonly lastUpdatedAtMs: number | null;
};

export type HostSessionListener = (
	nextState: HostSessionSnapshot,
	prevState: HostSessionSnapshot
) => void;

export type HostSessionEvent =
	| { type: "TRANSPORT_READY" }
	| {
			type: "CONFIG_RECEIVED";
			payload: {
				config: HostSessionConfig;
				freeAreas: FreeArea[];
			};
	  }
	| {
			type: "CONFIGURED_FREE_AREAS_UPDATED";
			payload: {
				freeAreas: FreeArea[];
			};
	  }
	| {
			type: "FREE_AREAS_UPDATED";
			payload: {
				freeAreas: FreeArea[];
			};
	  }
	| {
			type: "ORIENTATION_UPDATED";
			payload: {
				orientation: OrientationType;
			};
	  }
	| { type: "STARTUP_PRELOAD_RECEIVED" }
	| { type: "GAME_READY_RECEIVED" }
	| { type: "RESET"; reason: string };

export function createInitialHostSessionState(): HostSessionState {
	return {
		phase: "disconnected",
		transportReady: false,
		configured: false,
		startupPreloadRequested: false,
		gameReady: false,
		config: null,
		configuredFreeAreas: [],
		freeAreas: [],
		lastUpdatedAtMs: null,
	};
}

export function isHostConfigured(state: HostSessionSnapshot): boolean {
	return state.configured && state.config !== null;
}

export function isHostGameReady(state: HostSessionSnapshot): boolean {
	return state.gameReady;
}

export function isProviderBootEligible(state: HostSessionSnapshot): boolean {
	if (!isHostConfigured(state)) {
		return false;
	}

	const config = state.config;
	if (!config) {
		return false;
	}

	return (
		config.sessionKey.trim().length > 0 &&
		config.serverUrl.trim().length > 0 &&
		config.gameName.trim().length > 0 &&
		config.lang.trim().length > 0
	);
}

export function buildHostProviderBootInput(
	state: HostSessionSnapshot
): HostProviderBootInput | null {
	if (!isProviderBootEligible(state) || !state.config) {
		return null;
	}

	return {
		sessionKey: state.config.sessionKey,
		serverUrl: state.config.serverUrl,
		gameName: state.config.gameName,
		lang: state.config.lang,
		operator: state.config.operator,
		platform: state.config.platform,
		orientation: state.config.orientation,
		integrationType: state.config.integrationType,
	};
}
